import React, { useState } from 'react';
import ModuleSummaryModal from '../../components/common/ModuleSummaryModal';
import TraineeSummaryModal from '../../components/common/TraineeSummaryModal';
import PersonalModuleSummaryModal from '../../components/common/PersonalModuleSummaryModal';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  TrendingUp, Users, BookOpen, Clock, Award, Download, ChevronRight,
} from 'lucide-react';
import { useRoleData } from '../../hooks/useRoleData';
import './Analytics.css';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="analytics__tooltip">
        <p className="analytics__tooltip-label">{label}</p>
        {payload.map((p) => (
          <p key={p.name} style={{ color: p.color }}>
            {p.name}: <strong>{p.value}</strong>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const Analytics = () => {
  const { filteredAnalytics, filteredUsers, filteredModules, currentUser } = useRoleData();
  const [previewModuleRef, setPreviewModuleRef] = useState(null);
  const [previewTraineeRef, setPreviewTraineeRef] = useState(null);
  const [previewPersonalModuleRef, setPreviewPersonalModuleRef] = useState(null);
  const {
    monthlyActivity = [], moduleCompletion = [], contentCategories = [],
    stats = { totalContent: 0, activeModules: 0, enrolledUsers: 0, avgCompletionRate: 0, totalSessions: 0, totalCompletions: 0 },
    personalAnalytics,
  } = filteredAnalytics;
  const isTrainee = currentUser?.role === 'Trainee';

  // Build a lookup map for trainee's per-module progress from backend data
  const moduleProgressMap = {};
  if (isTrainee && personalAnalytics?.moduleProgress) {
    personalAnalytics.moduleProgress.forEach((mp) => {
      moduleProgressMap[mp.moduleId] = mp;
    });
  }

  const moduleCompletionMap = moduleCompletion.reduce((accumulator, item) => {
    if (item?.name) {
      accumulator[item.name] = item;
    }
    return accumulator;
  }, {});

  const moduleSummaryRows = filteredModules.map((module) => {
    const overviewMetrics = moduleCompletionMap[module.title];
    const traineeIds = Array.isArray(module.traineeIds) ? module.traineeIds : [];
    const traineeProgress = moduleProgressMap[module.id];
    const progressValue = isTrainee && traineeProgress
      ? traineeProgress.progress
      : overviewMetrics?.rate ?? module.completionRate ?? 0;
    const enrolledCount = overviewMetrics?.enrolled ?? module.traineeCount ?? traineeIds.length;

    return {
      ...module,
      enrolledCount,
      progressValue,
      statusLabel: isTrainee && traineeProgress
        ? (traineeProgress.completed ? 'Completed' : traineeProgress.progress > 0 ? 'In Progress' : 'Not Started')
        : module.status,
      statusBadge: isTrainee && traineeProgress
        ? (traineeProgress.completed ? 'badge-green' : traineeProgress.progress > 0 ? 'badge-yellow' : 'badge-gray')
        : (module.status === 'Active' ? 'badge-green' : 'badge-gray'),
    };
  });

  const trainees = filteredUsers.filter((u) => u.role === 'Trainee');
  const topTrainees = [...trainees]
    .filter((u) => u.status === 'Active')
    .sort((a, b) => (b.progress || 0) - (a.progress || 0))
    .slice(0, 6);

  return (
    <div className="analytics">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1>
            {currentUser?.role === 'Instructor' ? 'Module Analytics' :
             currentUser?.role === 'Trainee' ? 'My Progress' :
             'Analytics & Reports'}
          </h1>
          <p>
            {currentUser?.role === 'Instructor' ? 'Performance metrics for your assigned modules' :
             currentUser?.role === 'Trainee' ? 'Track your training progress and completion' :
             'Training performance metrics and progress overview'}
          </p>
        </div>
        <div className="analytics__header-actions">
          {currentUser?.role !== 'Trainee' && (
            <button className="btn btn-outline">
              <Download size={14} />
              Export Report
            </button>
          )}
        </div>
      </div>

      {/* KPI Row */}
      <div className={`analytics__stats ${isTrainee ? 'analytics__stats--trainee' : ''}`}>
        {[
          { icon: Clock, label: 'Total Sessions', value: (stats?.totalSessions ?? 0).toLocaleString(), color: 'green' },
          { icon: Award, label: 'Completions', value: (stats?.totalCompletions ?? 0).toLocaleString(), color: 'dark' },
          ...(currentUser?.role !== 'Trainee' ? [
            { icon: Users, label: 'Active Trainees', value: trainees.filter((u) => u.status === 'Active').length, color: 'blue' },
          ] : []),
          { icon: TrendingUp, label: 'Avg. Completion', value: `${stats?.avgCompletionRate ?? 0}%`, color: 'yellow' },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className={`analytics__stat-card analytics__stat-card--${color}`}>
            <div className="analytics__stat-icon">
              <Icon size={20} />
            </div>
            <div>
              <span className="analytics__stat-value">{value}</span>
              <span className="analytics__stat-label">{label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* HERO — Module Performance (most actionable surface, promoted to top) */}
      <div className="card analytics__hero">
        <div className="card-header">
          <div>
            <h3>
              {currentUser?.role === 'Instructor' ? 'My Modules Performance' :
               currentUser?.role === 'Trainee' ? 'My Modules Progress' :
               'Module Performance'}
            </h3>
            <p className="text-sm text-muted">
              {isTrainee ? 'Click any module to view your progress details' : 'Click any module to view detailed analytics'}
            </p>
          </div>
          <span className="badge badge-dark">{moduleSummaryRows.length} modules</span>
        </div>

        {isTrainee ? (
          /* ── Trainee: card-based module layout ── */
          moduleSummaryRows.length === 0 ? (
            <div className="analytics__empty">
              <BookOpen size={28} style={{ opacity: 0.35 }} />
              <p>No module performance data is available for this view yet.</p>
            </div>
          ) : (
            <div className="tmod-list">
              {moduleSummaryRows.map((m) => {
                const pct = m.progressValue;
                const statusCls = m.statusLabel === 'Completed' ? 'done' : pct > 0 ? 'active' : 'idle';
                const accent = pct >= 75 ? '#91cc47' : pct >= 50 ? '#d1dc24' : '#003a0f';

                return (
                  <div
                    key={m.id}
                    className={`tmod-card tmod-card--${statusCls}`}
                    onClick={() => {
                      const ref = m.moduleId ? m.moduleId.replace(/-/g, '') : m.id;
                      setPreviewPersonalModuleRef(ref);
                    }}
                    title="View my module progress"
                  >
                    {/* Left accent strip + mini ring */}
                    <div className="tmod-card__gauge">
                      <svg width="44" height="44" viewBox="0 0 44 44">
                        <circle cx="22" cy="22" r="18" fill="none" stroke="var(--color-dark-green-light)" strokeWidth="4" />
                        <circle cx="22" cy="22" r="18" fill="none" stroke={accent} strokeWidth="4"
                          strokeLinecap="round"
                          strokeDasharray={`${2 * Math.PI * 18}`}
                          strokeDashoffset={`${2 * Math.PI * 18 * (1 - pct / 100)}`}
                          transform="rotate(-90 22 22)"
                          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
                        />
                      </svg>
                      <span className="tmod-card__gauge-pct">{pct}%</span>
                    </div>

                    {/* Body */}
                    <div className="tmod-card__body">
                      <div className="tmod-card__title-row">
                        <span className="tmod-card__title">{m.title}</span>
                        <span className={`badge ${m.statusBadge}`}>{m.statusLabel}</span>
                      </div>
                      <div className="tmod-card__meta">
                        <span className="badge badge-dark">{m.category}</span>
                        <span>{m.duration} min</span>
                      </div>
                      <div className="tmod-card__bar">
                        <div className="tmod-card__bar-track">
                          <div className="tmod-card__bar-fill" style={{ width: `${pct}%`, background: accent }} />
                        </div>
                      </div>
                    </div>

                    {/* Chevron */}
                    <ChevronRight size={16} className="tmod-card__chevron" />
                  </div>
                );
              })}
            </div>
          )
        ) : (
          /* ── Admin / Instructor: table layout ── */
          <div className="analytics__table">
            <div className="analytics__table-header">
              <span>Module</span>
              <span>Category</span>
              <span>Enrolled</span>
              <span>Duration</span>
              <span>Completion</span>
              <span>Status</span>
            </div>
            {moduleSummaryRows.length === 0 && (
              <div className="analytics__empty">
                <BookOpen size={28} style={{ opacity: 0.35 }} />
                <p>No module performance data is available for this view yet.</p>
              </div>
            )}
            {moduleSummaryRows.map((m) => {
              const progressVal = m.progressValue;

              return (
                <div
                  key={m.id}
                  className="analytics__table-row analytics__table-row--clickable"
                  onClick={() => {
                    const ref = m.moduleId ? m.moduleId.replace(/-/g, '') : m.id;
                    setPreviewModuleRef(ref);
                  }}
                  title="Preview module analytics"
                >
                  <div className="analytics__table-module">
                    <div className="analytics__table-icon">
                      <BookOpen size={14} />
                    </div>
                    <span>{m.title}</span>
                  </div>
                  <span className="badge badge-dark">{m.category}</span>
                  <span className="analytics__table-cell">{m.enrolledCount} trainees</span>
                  <span className="analytics__table-cell">{m.duration} min</span>
                  <div className="analytics__table-progress">
                    <div className="progress-bar" style={{ height: 6, flex: 1 }}>
                      <div className="progress-bar-fill" style={{ width: `${progressVal}%` }} />
                    </div>
                    <span
                      className={`analytics__table-pct ${
                        progressVal >= 75 ? 'analytics__table-pct--high' :
                        progressVal >= 50 ? 'analytics__table-pct--mid' : 'analytics__table-pct--low'
                      }`}
                    >
                      {progressVal}%
                    </span>
                  </div>
                  <div className="analytics__table-status">
                    <span className={`badge ${m.statusBadge}`}>{m.statusLabel}</span>
                    <ChevronRight size={14} className="analytics__row-chevron" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Charts Row 1 — Activity (wide) + Category donut (narrow) */}
      <div className="analytics__charts-grid">
        <div className="card analytics__chart-card">
          <div className="card-header">
            <div>
              <h3>{isTrainee ? 'My Training Activity' : 'Training Activity'}</h3>
              <p className="text-sm text-muted">
                {isTrainee ? 'Your monthly sessions vs completions' : 'Monthly sessions vs completions'}
              </p>
            </div>
          </div>
          <div className="card-body analytics__chart-body">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={monthlyActivity} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#6b7280' }} />
                <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                <Line type="monotone" dataKey="sessions" stroke="#113696" strokeWidth={2.5} dot={{ fill: '#113696', r: 4 }} name="Sessions" />
                <Line type="monotone" dataKey="completions" stroke="#91cc47" strokeWidth={2.5} dot={{ fill: '#91cc47', r: 4 }} name="Completions" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card analytics__chart-card">
          <div className="card-header">
            <h3>{isTrainee ? 'My Content Categories' : 'Content Categories'}</h3>
          </div>
          <div className="card-body analytics__chart-body">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={contentCategories} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3} dataKey="value">
                  {contentCategories.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value}`, 'Items']} />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Charts Row 2 — Module completion bar + Top performers / Personal progress */}
      <div className="analytics__charts-grid">
        <div className="card analytics__chart-card">
          <div className="card-header">
            <div>
              <h3>{isTrainee ? 'My Module Progress' : 'Module Completion Rates'}</h3>
              <p className="text-sm text-muted">
                {isTrainee
                  ? 'Your completion progress in each enrolled module'
                  : '% of enrolled trainees who completed each module'}
              </p>
            </div>
          </div>
          <div className="card-body analytics__chart-body">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={moduleCompletion} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6b7280' }} interval={0} angle={-10} />
                <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} domain={[0, 100]} />
                <Tooltip content={<CustomTooltip />} formatter={(v) => [`${v}%`, isTrainee ? 'Progress' : 'Completion']} />
                <Bar dataKey="rate" name={isTrainee ? 'Progress %' : 'Completion %'} radius={[6, 6, 0, 0]}>
                  {moduleCompletion.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.rate >= 75 ? '#91cc47' : entry.rate >= 50 ? '#d1dc24' : '#003a0f'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card analytics__chart-card">
          <div className="card-header">
            <h3>{isTrainee ? 'My Overall Progress' : 'Top Performers'}</h3>
            {!isTrainee && <span className="badge badge-dark">{topTrainees.length}</span>}
          </div>
          <div className="card-body analytics__progress-list">
            {isTrainee && personalAnalytics ? (
              <div className="analytics__personal">
                <div className="analytics__personal-row">
                  <span className="analytics__trainee-name">Overall Progress</span>
                  <span className="analytics__trainee-pct">{personalAnalytics.overallProgress || 0}%</span>
                </div>
                <div className="progress-bar" style={{ height: 8, marginBottom: 'var(--space-4)' }}>
                  <div className="progress-bar-fill" style={{ width: `${personalAnalytics.overallProgress || 0}%` }} />
                </div>
                <div className="analytics__personal-grid">
                  <div className="analytics__personal-stat">
                    <span className="analytics__stat-value">{personalAnalytics.totalModules || 0}</span>
                    <span className="analytics__stat-label">Enrolled</span>
                  </div>
                  <div className="analytics__personal-stat">
                    <span className="analytics__stat-value">{personalAnalytics.completedModules || 0}</span>
                    <span className="analytics__stat-label">Completed</span>
                  </div>
                </div>
              </div>
            ) : topTrainees.length === 0 ? (
              <div className="analytics__empty">
                <Users size={28} style={{ opacity: 0.35 }} />
                <p>No active trainees yet.</p>
              </div>
            ) : (
              topTrainees.map((u, idx) => (
                <div
                  key={u.id}
                  className="analytics__trainee-row analytics__trainee-row--clickable"
                  onClick={() => setPreviewTraineeRef(u.userUuid ? u.userUuid.replace(/-/g, '') : u.id)}
                  title="Preview trainee analytics"
                >
                  <span className="analytics__rank">#{idx + 1}</span>
                  <div className="avatar" style={{ width: 32, height: 32, fontSize: '0.7rem', flexShrink: 0 }}>
                    {u.avatar ? <img src={u.avatar} alt={u.name} /> : u.initials}
                  </div>
                  <div className="analytics__trainee-info">
                    <span className="analytics__trainee-name">{u.name}</span>
                    <div className="analytics__trainee-progress">
                      <div className="progress-bar" style={{ height: 6 }}>
                        <div className="progress-bar-fill" style={{ width: `${u.progress}%` }} />
                      </div>
                    </div>
                  </div>
                  <span className="analytics__trainee-pct">{u.progress}%</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <ModuleSummaryModal
        isOpen={!!previewModuleRef}
        onClose={() => setPreviewModuleRef(null)}
        moduleRef={previewModuleRef}
      />
      <TraineeSummaryModal
        isOpen={!!previewTraineeRef}
        onClose={() => setPreviewTraineeRef(null)}
        traineeRef={previewTraineeRef}
      />
      <PersonalModuleSummaryModal
        isOpen={!!previewPersonalModuleRef}
        onClose={() => setPreviewPersonalModuleRef(null)}
        moduleRef={previewPersonalModuleRef}
      />
    </div>
  );
};

export default Analytics;
